import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { filter, map } from 'rxjs/operators';
import { TourStep } from '../models/tour-step.model';

export type TourRequestTarget =
  | 'dashboard'
  | 'sidebar'
  | 'ib-dashboard'
  | 'ib-sidebar';

export type PostSignupTourTarget = 'trader' | 'ib';

@Injectable({ providedIn: 'root' })
export class TourService {
  private steps: TourStep[] = [];
  private currentStepIndex$ = new BehaviorSubject<number>(-1);
  private activeSource = new BehaviorSubject<boolean>(false);
  readonly active$ = this.activeSource.asObservable();
  private startTour$ = new Subject<void>();
  private tourRequestSource = new Subject<TourRequestTarget>();
  private dashboardFinishedSource = new Subject<void>();
  readonly dashboardFinished$ = this.dashboardFinishedSource.asObservable();
  private tourEndedSource = new Subject<void>();
  readonly tourEnded$ = this.tourEndedSource.asObservable();
  private ibConvertedSource = new Subject<void>();
  readonly ibConverted$ = this.ibConvertedSource.asObservable();

  private startTimer?: ReturnType<typeof setTimeout>;

  private readonly POST_SIGNUP_TOUR_KEY = 'post-signup-tour-pending';

  readonly currentIndex$ = this.currentStepIndex$.asObservable();

  readonly step$ = this.currentStepIndex$.pipe(
    map((index) => (index >= 0 ? this.steps[index] ?? null : null))
  );

  readonly total$ = new BehaviorSubject<number>(0);

  start(steps: TourStep[]): void {
    this.clearStartTimer();

    this.activeSource.next(true);

    this.currentStepIndex$.next(-1);
    this.steps = [];
    this.total$.next(0);

    this.startTimer = setTimeout(() => {
      this.steps = steps;
      this.total$.next(steps.length);

      if (steps.length > 0) {
        this.currentStepIndex$.next(0);
      } else {
        this.end();
      }
    }, 400);
  }

  next(): void {
    const nextIndex = this.currentStepIndex$.value + 1;

    if (nextIndex >= this.steps.length) {
      this.end();
      return;
    }

    this.currentStepIndex$.next(nextIndex);
  }

  previous(): void {
    const index = this.currentStepIndex$.value;
    if (index > 0) {
      this.currentStepIndex$.next(index - 1);
    }
  }

  end(): void {
    this.clearStartTimer();

    this.currentStepIndex$.next(-1);
    this.steps = [];
    this.total$.next(0);
    this.activeSource.next(false);

    this.tourEndedSource.next();
  }


  completeDashboardTour(): void {
    this.dashboardFinishedSource.next();
  }


  requestTour(target: TourRequestTarget): void {
    this.tourRequestSource.next(target);
  }

  onTourRequest(target: TourRequestTarget): Observable<void> {
    return this.tourRequestSource.pipe(
      filter((requestedTarget) => requestedTarget === target),
      map(() => undefined)
    );
  }


  markPostSignupTourPending(target: PostSignupTourTarget): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.sessionStorage.setItem(this.POST_SIGNUP_TOUR_KEY, target);
  }

  hasPostSignupTourPending(target: PostSignupTourTarget): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.sessionStorage.getItem(this.POST_SIGNUP_TOUR_KEY) === target;
  }

  consumePostSignupTourPending(target: PostSignupTourTarget): boolean {
    if (!this.hasPostSignupTourPending(target)) {
      return false;
    }

    window.sessionStorage.removeItem(this.POST_SIGNUP_TOUR_KEY);

    return true;
  }


  notifyIbConverted(): void {
    this.ibConvertedSource.next();
  }


  requestStartTour(): void {
    this.startTour$.next();
  }

  onStartTour(): Observable<void> {
    return this.startTour$.asObservable();
  }

  private clearStartTimer(): void {
    if (this.startTimer) {
      clearTimeout(this.startTimer);
      this.startTimer = undefined;
    }
  }
}
